import { Link } from 'react-router-dom';

import { useDocumentTitle } from '../lib/useDocumentTitle';
import { SUPPORT_EMAIL, SUPPORT_LOCATION } from '../lib/support';

// The App Store Privacy Policy URL (TOM-98). Every statement here is meant to
// be true of the code on main; when a feature changes what we collect, send or
// keep, change this page in the same PR.
export function Privacy() {
  useDocumentTitle('Neomaris | Privacy policy');

  const supportLink = (
    <a className="link" href={`mailto:${SUPPORT_EMAIL}`}>
      {SUPPORT_EMAIL}
    </a>
  );

  return (
    <section className="legal-prose">
      <h2>Privacy policy</h2>
      <p className="legal-date">Effective date: November 2, 2026</p>

      <p>
        Neomaris makes boat-monitoring software: Neomaris Core, which runs on a computer aboard your
        boat (the gateway), the website at tombolomarine.com, and the Neomaris app for iPhone and
        iPad. This policy says what we collect, why, who else handles it, how long we keep it, and
        what you can do about it. We do not sell your personal information and we show no ads. We
        use one analytics tool, PostHog, to learn how people use the website and the app (section
        7).
      </p>

      <h3>1. Information we collect</h3>
      <ul>
        <li>
          <strong>Your account:</strong> your email address and your password, which we store only
          as a one-way hash. If you sign in with Google instead, we receive your email address and
          the identifier Google gives your account. If you sign in with Apple, we receive the email
          address you share with Apple, which may be a private relay address, the identifier Apple
          gives your account, and your name the first time you sign in (section 5). Your display
          name, if you add one, and your settings, such as units.
        </li>
        <li>
          <strong>Your boats:</strong> what Neomaris Core sends us from the boat: its name, the
          devices aboard and their settings, sensor readings and their history, the boat&apos;s
          position and track, the AIS traffic its receiver hears, its alarms and their history, and
          the engine and voyage logs we work out from that data.
        </li>
        <li>
          <strong>Phones and tablets you pair:</strong> a label such as &quot;Neomaris app ·
          iPhone&quot;, and when it was paired and last used. If you allow notifications, the
          app&apos;s push token, its version, your language and time zone, and whether you allowed
          Critical Alerts (section 4).
        </li>
        <li>
          <strong>Your conversations with Neo,</strong> the Neomaris AI assistant: your questions,
          typed or spoken, its answers, your ratings of them, and the photos and PDFs you attach
          (section 3). For a voice call, a record of the call itself (section 3).
        </li>
        <li>
          <strong>A record of AI use:</strong> for each request to an AI model made for you, which
          model answered, how much text it processed and what it cost.
        </li>
        <li>
          <strong>Messages you send us,</strong> by email or with the contact form. The contact form
          emails your name, email address and message to our support inbox; we do not store it
          anywhere else.
        </li>
        <li>
          <strong>Request logs:</strong> our server logs each request it receives, with your IP
          address, the address requested, the time and the result. We do not log what you send in a
          request.
        </li>
        <li>
          <strong>How you use the website and the app:</strong> which pages and screens you open and
          which buttons and links you tap, with your browser or device, its operating system and
          screen size, and the site that sent you to us. When you are signed in, this is linked to
          your account identifier and your email address. It never includes the words on the screen,
          what you type, your boat&apos;s readings or position, or your conversations with Neo
          (section 7).
        </li>
      </ul>

      <h3>2. How we use your information</h3>
      <p>We use your information to:</p>
      <ul>
        <li>Show you your boat, its instruments, its position and its history</li>
        <li>Run your alarms and tell you when one sounds, clears, or your boat goes quiet</li>
        <li>
          Answer your questions through Neo, our AI assistant, and watch over your boat with the AI
          Watch
        </li>
        <li>Write your boat&apos;s morning and evening reports</li>
        <li>Set up your boat&apos;s alarms and devices from the equipment we find aboard</li>
        <li>Answer you when you write to us, and tell you about your account</li>
        <li>Keep the service running and secure, and find and fix problems</li>
        <li>See which parts of the website and the app people use, so we can improve them</li>
      </ul>
      <p>We do not use your information for advertising.</p>

      <h3>3. Neo, the AI Watch, and AI models</h3>
      <p>
        Neo, our assistant, and the AI Watch use large language models run by other companies. To
        answer a question or review your boat, we send the model what the task needs: your question
        and the earlier messages in that conversation, your display name, your boats&apos; names,
        and data about your boat such as its devices, alarms, sensor readings, weather forecast,
        and, when the question needs it, its position. We do not send your email address or
        password. Answers come from AI models and can be wrong; check anything that matters to your
        safety against your instruments.
      </p>
      <ul>
        <li>
          <strong>Anthropic</strong> runs Claude. Neo answers with Claude Opus 5.5 unless you choose
          another model (type /model in the chat to choose Claude Sonnet 5.5, Claude Haiku 4.5 or
          DeepSeek V4 Pro). Claude also writes conversation summaries and what Neo learns from them
          (below), whichever model you chat with. Under Anthropic&apos;s commercial terms it does
          not use this data to train its models, and it deletes it within 30 days unless its
          automated safety systems flag a request as violating its usage policy or the law requires
          it to keep the data longer.
        </li>
        <li>
          <strong>OpenRouter</strong> routes requests to DeepSeek models: the AI Watch, Neo when you
          choose DeepSeek V4 Pro, and the two sentences that open your boat&apos;s morning and
          evening reports. For a report we send only the boat&apos;s name and a plain judgement of
          each line, such as &quot;ok&quot; or &quot;needs attention&quot;, never its readings. For
          every request we send through OpenRouter, these and the equipment requests described
          below, we allow only providers that neither retain the data nor use it to train their
          models. When Neo draws you a picture, such as how an anchor bridle attaches, it writes a
          short description of what to draw, leaving out your boat&apos;s name, your name, and your
          boat&apos;s readings and position, and we check for those before the description goes to
          an image model through OpenRouter. Only that description is sent. The image model&apos;s
          provider may keep it for a while, and some image models run under their provider&apos;s
          own data terms, without the limits we set on our other requests.
        </li>
        <li>
          <strong>ElevenLabs</strong> hears and speaks a voice call with Neo (below).
        </li>
        <li>
          <strong>TypeSafe</strong> runs Jev, a model that screens each message you send Neo for
          abuse and for attempts to misuse it, before Neo answers. We send TypeSafe that one message
          and nothing else: no earlier messages, no name, no boat data. TypeSafe does not train or
          fine-tune models on what we send; it keeps it for as long as it needs to provide the
          service and does not state a fixed period. Its service is hosted in the United States.
          When TypeSafe cannot be reached, we send the message to the same model through OpenRouter,
          which allows only providers that neither retain the data nor use it to train their models.
        </li>
      </ul>
      <p>
        The AI Watch reviews your boat&apos;s data while someone has the boat&apos;s Watch open. It
        also reviews it around the clock for a boat where an owner has a paired phone with
        notifications on and Watch notices on; each owner can turn Watch notices off for a boat.
        Chat with Neo sends nothing to a model until you ask a question.
      </p>
      <p>
        We also use AI models through OpenRouter (DeepSeek V4 Pro by default) to work out your
        boat&apos;s equipment: to suggest alarms, to work out which device supplies which data, and
        to name devices. These requests carry your devices&apos; makes, models and settings and your
        boat&apos;s alarms, not your conversations or account information. To find manuals for your
        devices, we search the web by make and model through Serper and Tavily, and an AI model
        through OpenRouter picks the manual from the results. The searches carry a device&apos;s
        make and model. The AI model gets the device&apos;s catalog details, such as its make, model
        and product code, and the search results. Neither carries anything about you or your boat.
        These equipment requests go only to OpenRouter providers that neither retain the data nor
        use it to train their models.
      </p>
      <p>
        <strong>Photos and PDFs.</strong> You can attach photos and PDFs to a chat with Neo. We
        store each file privately, and only you can open it, from the chat it was sent in; our staff
        tools do not show attachments. A file goes only to a model that can read it, which today
        means Claude, at Anthropic. If you have chosen DeepSeek, the file is not sent and Neo
        answers without it. Photos are sent as you took them: if a photo carries its location in its
        metadata, that location goes to Anthropic too, so remove it first if you do not want to
        share it. A file you attach but never send is deleted after a day. The others are kept with
        the chat, as below.
      </p>
      <p>
        <strong>Voice calls.</strong> In the app and on our website you can talk with Neo in a live
        voice call. ElevenLabs runs the voice for us, as our processor. While a call is on, the
        sound from your microphone streams to ElevenLabs over its real-time audio connection, which
        is built on LiveKit. ElevenLabs turns what you say into text and sends us the text; Neo
        answers it as it answers a typed question, with the same AI models and the same data as
        above; and we send ElevenLabs the words Neo says back, which it speaks in a synthetic voice.
        ElevenLabs also sees your IP address. No ElevenLabs model writes Neo&apos;s answers. We have
        set up our ElevenLabs voice service with voice recording off and in its zero retention mode,
        so ElevenLabs does not keep the call&apos;s audio or text once it has processed it. We store
        no audio. What you said, as ElevenLabs heard it, and Neo&apos;s answers are kept as messages
        in the chat, like typed ones, and we keep a record of each call: its boat and chat, when it
        started and ended, how long it lasted, how it ended, and ElevenLabs&apos; identifier for it.
        The record is deleted with your account or the boat. Your microphone is used only while a
        call is on.
      </p>
      <p>
        <strong>Your conversations.</strong> We keep your conversations with your account so Neo can
        follow the thread. When you delete a chat, it disappears for you at once, and Neo forgets
        its summary of it and anything it learned from that summary at once. The chat itself, with
        any files attached to it, is kept until you delete your account or the boat it belongs to is
        erased; there is no timed purge. Authorized Neomaris staff may review conversations, deleted
        ones included, to fix problems and improve Neo; each review requires a stated reason and is
        logged.
      </p>
      <p>
        <strong>Try it.</strong> When you try Neomaris without an account, you use a demonstration
        boat under a temporary guest account that is not tied to your name or email address. When
        your visit ends, we close the guest account: you are signed out, the boat goes back to the
        next visitor, and Neo forgets what it learned about you. We keep your chats with Neo from
        the visit, with any files you attached, for 90 days after the visit ends, then delete them.
        Authorized Neomaris staff may review them in that time to fix problems and improve Neo; each
        review requires a stated reason and is logged.
      </p>
      <p>
        <strong>What Neo remembers.</strong> Neo keeps three kinds of memory about you:
      </p>
      <ul>
        <li>
          <strong>Saved notes:</strong> short notes the assistant keeps when you ask it to, such as
          where you keep your boat or how you like your answers.
        </li>
        <li>
          <strong>Conversation summaries:</strong> once a chat has been quiet for half an hour, we
          send it (your messages and the assistant&apos;s answers) to Anthropic&apos;s Claude to
          write a short summary: a title, a few sentences on what you asked and what the assistant
          found, facts you stated about yourself or your boat, and questions left open. This happens
          even if you have chosen a non-Claude model for your chats. Summaries are on by default;
          you can switch them off on your Profile page. The model is told to leave out passwords,
          codes, keys, payment and identity numbers, and other people&apos;s personal details, and
          an automatic check discards summary text that mentions passwords, keys, or payment or
          identity numbers. These checks can miss things, so review your summaries on your Profile
          page. We summarise only chats from after the feature was switched on for your account.
        </li>
        <li>
          <strong>What the assistant has learned about you:</strong> a short list, at most 20 items,
          of lasting facts and preferences, such as how you like your answers or what is aboard your
          boat. At most once a day, after your first summary and then after five new ones or once a
          week has passed, we send your newer summaries (their titles, short descriptions and the
          facts you stated), your saved notes and the current list to Anthropic&apos;s Claude, which
          updates the list. When you forget a summary, the items recorded as learned from it are
          deleted at once, and we send your remaining summaries the same way to rebuild the list. An
          item can also hold something from a summary it does not record, so to be sure a fact is
          gone, forget the item itself. This happens even if you have chosen a non-Claude model for
          your chats. The model is told to keep only lasting facts and preferences you stated, and
          habits that recur across several of your conversations, and to leave out the same details
          as summaries do, and the same automatic check applies. When you forget an item, we keep a
          fingerprint of it so it is not learned again: a one-way code made from its wording and,
          when available, a numerical representation of its meaning, which we compute on our own
          server. We do not keep its words.
        </li>
      </ul>
      <p>
        We send your saved notes, what the assistant has learned about you and some of your
        conversation summaries with your later questions to the model that answers them (Anthropic,
        unless you choose another model), so the assistant can pick up where you left off. With each
        question we send a few summaries: the most recent ones and the ones that best match what you
        asked. We leave out summaries of chats held on the page of a boat you no longer own and,
        when you ask from one boat&apos;s page, of chats held on another boat&apos;s page. In the
        same way, we leave out anything learned about a boat you no longer own and, when you ask
        from one boat&apos;s page, anything learned about another boat.
      </p>
      <p>
        When you ask the assistant about an earlier chat, or ask it to forget one, it can also
        search all your summaries, those of chats held on any boat you still own included, and send
        the ones it finds to the model that answers you. It can then read one of those chats back:
        we send that chat&apos;s own messages, yours and the assistant&apos;s, up to about 4,000
        characters, to the model you use now, which may not be the one you used then. It never
        searches or reads back a chat held on the page of a boat you no longer own.
      </p>
      <p>
        Only you can see your notes, summaries and what the assistant has learned about you, and
        only your own are sent with your questions, even on a boat you share with other owners. On
        your Profile page you can view and delete each one, and switch conversation summaries off,
        which erases all of them and everything learned from them, fingerprints included. You can
        also ask the assistant to forget a summary, which deletes the items recorded as learned from
        it just as forgetting it on your Profile page does, or to forget everything it has learned
        about you, which forgets every item and keeps a fingerprint of each. Forgetting a summary
        does not delete the chat itself. We keep each summary for a year after its chat ended, then
        delete it. Your notes, summaries, what the assistant has learned and its fingerprints are
        deleted with your account.
      </p>
      <p>
        <strong>How we test the assistant.</strong> When the assistant gets an answer wrong, for
        example one you rated thumbs down or one that the assistant or our safety checks declined,
        our staff may review it, with the same logged access, and turn it into a test case so later
        versions of the assistant get it right. Our staff write the test question in their own
        words; we never keep the text of your conversation in a test case. A test case is kept after
        your account is deleted, with no link to your account.
      </p>
      <p>
        A test case runs on a fictional demonstration boat, not on your vessel&apos;s data, unless
        the vessel&apos;s owner has given us explicit consent to use recorded moments. A recorded
        moment is a copy of the vessel&apos;s data up to the time of a question: its name, the names
        of its devices, its sensor readings and their history, its last recorded position at that
        time, its track (every position we hold for it up to then), and the engine and voyage logs
        worked out from that data. The owner&apos;s consent covers all of this, including the
        position and the track. We keep a record of each consent. An owner can withdraw consent at
        any time. We then stop using that vessel&apos;s data in new test cases and remove its
        recorded moments from the tests we run. Earlier versions of our test files, kept in our
        source code history, may still contain them. Deleting the owner&apos;s account, or erasing
        the vessel and its data, withdraws the consent in the same way, with the same exception for
        our source code history. We keep the record of the consent and of its withdrawal after the
        deletion.
      </p>
      <p>
        <strong>Your choices.</strong> You can choose which model answers you, switch conversation
        summaries off on your Profile page, and delete chats, notes, summaries and learned items as
        described above.
      </p>

      <h3>4. Notifications on your phone</h3>
      <p>
        When an alarm on your boat sounds or clears, or your boat goes quiet or comes back online,
        we send a notification to each phone and tablet you have paired and allowed notifications
        on, and, unless you turn them off, one when the boat&apos;s morning or evening report is
        ready or the AI Watch finds something. Apple&apos;s Push Notification service carries it, so
        Apple handles the notification&apos;s text: the boat&apos;s name and the alarm&apos;s title,
        a line saying the boat has gone quiet, the report&apos;s title and summary, which carry no
        readings, or what the AI Watch found, as it wrote it. To send notifications we keep the
        details listed in section 1. Removing a phone from your account, by signing out of the app
        or at tombolomarine.com/devices, stops its notifications, and the registration is deleted
        with your account.
      </p>

      <h3>5. Signing in to the app</h3>
      <p>
        <strong>Sign in with Apple.</strong> If you sign in to the app with Apple, Apple tells us
        the identifier it gives your Apple account for Neomaris, which is how we know it is you next
        time, and the email address you chose to share. If you chose to hide your address, that is a
        private relay address Apple made for Neomaris: email we send to it goes through Apple, which
        forwards it to your own address, and you can stop it in your Apple Account settings. The
        first time you sign in, Apple also gives the app your name, unless you changed or removed
        it, and we make it your display name, which you can change on your Profile page. We use them
        to make your account, sign you in and write to you about your account. We never see your
        Apple password, and we keep none of Apple&apos;s sign-in tokens. When you delete your
        account, we ask Apple to revoke Neomaris&apos;s use of your Apple sign-in before we delete
        anything.
      </p>
      <p>
        When you sign in to the app with your email address and password, the app uses Apple&apos;s
        App Attest to show us the request comes from the genuine Neomaris app. We keep the key the
        app made for this, a public key and a counter, and do not link it to your account. We send
        nothing to Apple to check it.
      </p>

      <h3>6. Setting up your gateway</h3>
      <ul>
        <li>
          <strong>Wi-Fi passwords.</strong> When you join the gateway to a Wi-Fi network from its
          own setup page, the password goes only to the gateway on your boat. It is never sent to
          us.
        </li>
        <li>
          <strong>Passwords for your instruments&apos; gateways.</strong> If you type one into our
          setup page at tombolomarine.com, we pass it to your boat encrypted, and hold it for at
          most a minute while it is on its way. We do not keep it.
        </li>
        <li>
          <strong>What the gateway finds.</strong> Before a boat is claimed, Neomaris Core sends us
          what it found on the boat&apos;s network, such as your instruments&apos; makes, models and
          settings, which can include the boat&apos;s MMSI and position, so our setup page can show
          it. We delete it about 90 days after the boat stops sending it.
        </li>
      </ul>

      <h3>7. Other companies that handle your data</h3>
      <p>
        Besides Anthropic, OpenRouter, ElevenLabs and TypeSafe (section 3), these companies handle
        data for us:
      </p>
      <ul>
        <li>
          <strong>Hetzner</strong> runs the server that holds our database and runs the service.
        </li>
        <li>
          <strong>Cloudflare</strong> carries all traffic to tombolomarine.com. Its Turnstile check,
          on sign-up, the beta form (on the home page and the beta page), password reset, the
          contact form and signing in after a failed attempt, sees your IP address and your browser.
          Cloudflare also sends our emails (password resets, and contact form messages to us) and
          stores chat attachments and our nightly database backups.
        </li>
        <li>
          <strong>Apple</strong> delivers notifications (section 4), signs you in if you choose Sign
          in with Apple, and forwards our email to you if you hid your address from us (section 5).
          The app draws its maps with Apple Maps, so Apple sees the area a map shows and your IP
          address.
        </li>
        <li>
          <strong>Google</strong> signs you in if you choose Google sign-in. Our website serves its
          fonts from our own servers, not from Google. One unlisted page we use to compare chart
          fonts loads its fonts from Google, so Google sees your IP address if you open it.
        </li>
        <li>
          <strong>MapTiler</strong> draws the maps on our website: on your boat&apos;s dashboard and
          boards, and in Neo&apos;s answers. Your browser loads map images for the area a map shows
          from MapTiler, so MapTiler sees that area and your IP address, and its map software
          reports a session identifier to MapTiler.
        </li>
        <li>
          <strong>Open-Meteo</strong> provides weather forecasts and tides. We send it your
          boat&apos;s position rounded to about a kilometre.
        </li>
        <li>
          <strong>Serper</strong> and <strong>Tavily</strong> run the manual searches in section 3.
        </li>
        <li>
          <strong>SailLogger</strong>, only if you link your boat to it on the gateway. Neomaris
          Core then sends your boat&apos;s position and the AIS it hears to SailLogger directly.
        </li>
        <li>
          <strong>Porkbun</strong> forwards email sent to {SUPPORT_EMAIL} to our inbox.
        </li>
        <li>
          <strong>PostHog</strong> records how you use our website and the app, as section 1
          describes, on its servers in the United States. Your browser or the app sends it directly,
          so PostHog also sees your IP address, from which it works out a rough location. We remove
          the parts of a web address that can hold a code, such as a password reset link, before
          anything is sent. A tap is recorded by which button it was, never by the words on it, and
          we record no video of your screen. A Try it visit is recorded without an account or email
          address, and nothing is sent from our staff tools.
        </li>
      </ul>
      <p>
        We may also share information when the law requires it, or when it is needed to protect
        someone&apos;s safety.
      </p>

      <h3>8. Who else sees your boat</h3>
      <p>
        Everyone who owns a boat with you sees all of its data, including its position and track.
        Your chats, notes, summaries and attachments stay yours. We do not show other Neomaris users
        the data or position your gateway sends us. If your boat transmits AIS, other boats,
        Neomaris users&apos; included, can receive that radio broadcast directly. Because AIS is
        public, another Neomaris owner can also follow your boat as a friend boat and see its AIS
        position while it is within 300 km of their boat, as you can follow theirs. A friend
        boat&apos;s position comes from public AIS data (Data by Saillogger). Your list of friend
        boats holds each boat&apos;s MMSI and the name you give it, and is shared with your
        boat&apos;s other owners. When you ask Neo about a friend boat, its name, MMSI, distance and
        position go to the model that answers you. Authorized Neomaris staff can see account and
        boat data to support you and keep the service running.
      </p>

      <h3>9. Cookies and browser storage</h3>
      <p>
        Apart from PostHog&apos;s, below, our website sets only the cookies it needs to sign you in:
        two that hold your session, for 24 hours or, if you choose to stay signed in, a year; and
        one that lasts ten minutes during Google sign-in. It keeps a few preferences in your
        browser&apos;s storage, such as banners you dismissed and the map layers you chose, and the
        map keeps copies of map images there. PostHog (section 7) keeps a random identifier for your
        browser in a cookie and in your browser&apos;s storage for a year, so it can tell a
        returning visitor from a new one; signing out replaces it with a new one. We use no
        advertising cookies.
      </p>

      <h3>10. Security</h3>
      <p>
        Every connection to Neomaris is encrypted with TLS. Passwords and boat secrets are stored
        only as one-way hashes. No method of storage or transmission is completely secure, and we
        cannot guarantee absolute security.
      </p>

      <h3>11. How long we keep your data</h3>
      <ul>
        <li>Your account: until you delete it.</li>
        <li>
          Your boat&apos;s data, its reports included: for as long as the boat is on Neomaris.
          Deleting a boat from your dashboard hides it but keeps its data, so it can be restored. To
          erase a boat and all its data, email us.
        </li>
        <li>Your conversations and their attachments: as section 3 describes.</li>
        <li>Chats from a Try it visit, with their attachments: 90 days after the visit ends.</li>
        <li>Request logs: overwritten by newer logs as they fill a fixed amount of space.</li>
        <li>
          How you use the website and the app, at PostHog: for as long as PostHog keeps it under our
          plan. Deleting your account does not delete it on its own; email us and we will delete
          what is linked to your account.
        </li>
        <li>Our nightly database backups: deleted 30 days after they are made.</li>
      </ul>
      <p>
        When your account is deleted, we sign you out everywhere at once and erase your personal
        information within 30 days. A boat only you own is erased with your account, with all its
        data. On a boat you share, we remove you, and the other owners keep the boat and its data.
        Copies in our backups go when those backups are deleted. We keep a record that we deleted
        the account, with its email address, in our security log, and we keep test cases and consent
        records as section 3 describes, and anything the law requires us to keep.
      </p>

      <h3>12. Your rights and choices</h3>
      <p>You can:</p>
      <ul>
        <li>Change your display name, units and password on your Profile page</li>
        <li>Ask for a copy of the personal information we hold about you</li>
        <li>Ask us to correct information that is wrong</li>
        <li>Ask us to delete your account and its data, as section 11 describes</li>
      </ul>
      <p>
        To ask, email {supportLink} from the address you sign in with. The{' '}
        <Link className="link" to="/support">
          Support
        </Link>{' '}
        page has more help.
      </p>

      <h3>13. Children&apos;s privacy</h3>
      <p>
        Neomaris is not intended for children under 13 years of age. We do not knowingly collect
        personal information from children under 13.
      </p>

      <h3>14. Changes to this policy</h3>
      <p>
        We may update this Privacy Policy from time to time. We will notify you of material changes
        by posting the revised policy on this page with an updated effective date.
      </p>

      <h3>15. Contact</h3>
      <p>
        Questions about this policy, or about your data: {supportLink}. Neomaris is based in{' '}
        {SUPPORT_LOCATION}.
      </p>

      <p className="legal-back">
        <Link className="link" to="/">
          &larr; Back to home
        </Link>
      </p>
    </section>
  );
}
